/**
 * Parser för E.on Energidistributions CSV-export av timdata.
 *
 * Format:
 *   - UTF-8 med BOM
 *   - Semikolon-separerade kolumner
 *   - Komma som decimaltecken
 *   - Datum: "YYYY-MM-DD HH:MM:SS" i lokal tid (Europe/Stockholm)
 *   - Kvarttimmesintervall (00:00 → 00:15, etc — 4 mätningar/timme)
 *
 * Struktur:
 *   Rad 1: metadatarubriker
 *   Rad 2: metadatavärden (innehåller anläggnings-id — PII, ignoreras)
 *   Rad 3: tom
 *   Rad 4: datarubriker (Starttidpunkt;Sluttidpunkt;Energiriktning;Kvalitet;Kvantitet)
 *   Rad 5+: data
 *
 * Energiriktning är "Förbrukning" eller "Produktion" (för solägare).
 * Kvalitet är "Uppmätt" eller "Beräknat".
 *
 * Output: 1-timmesdata i HourPoint-format så resten av appen kan hantera
 * det identiskt med Tibber-data. Kvarter aggregeras genom summering.
 *
 * Sommartid: filen är i lokal tid utan tidszon. Den dag DST avslutas
 * (sista söndagen i okt) får vi 25 timmar med kvarter — vi aggregerar
 * alla 100 kvarter, vilket sätter ihop båda 02-timmarna i samma timme.
 * Det är en liten approximation men påverkar resultatet med <0.5%
 * eftersom det handlar om en enda dag per år.
 */
import type { HourPoint } from "../sim/types";

export interface ParseResult {
  hours: HourPoint[];
  /** Totala antalet rader i indatat (datarader, exklusive metadata) */
  rowsParsed: number;
  /** Hur många rader vi behövde skippa (felformat, ogiltigt värde) */
  rowsSkipped: number;
  /** Antal månader som datan täcker (sorterade unika YYYY-MM) */
  monthCount: number;
  /** Första och sista timestamp i datat */
  firstTimestamp: string;
  lastTimestamp: string;
  /** Total import (förbrukning) i kWh */
  annualImportKWh: number;
  /** Total export (produktion) i kWh — 0 om filen saknar produktionsrader */
  annualExportKWh: number;
  /** Andel rader som är "Beräknat" istället för "Uppmätt" — varning om hög */
  estimatedShare: number;
}

export class CsvParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CsvParseError";
  }
}

/**
 * Parsar E.on CSV. Kastar CsvParseError vid felformat.
 */
export function parseEonCsv(text: string): ParseResult {
  // Strippa BOM om den finns
  const cleaned = text.replace(/^\uFEFF/, "");
  const lines = cleaned.split(/\r?\n/);

  if (lines.length < 5) {
    throw new CsvParseError(
      "Filen är för kort. Förväntade minst 5 rader (metadata + data).",
    );
  }

  // Hitta datarubriken — den börjar med "Starttidpunkt;Sluttidpunkt;"
  let dataHeaderIdx = -1;
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    if (lines[i]?.startsWith("Starttidpunkt;Sluttidpunkt;Energiriktning")) {
      dataHeaderIdx = i;
      break;
    }
  }
  if (dataHeaderIdx === -1) {
    throw new CsvParseError(
      "Kände inte igen filformatet. Förväntade en rad som börjar med " +
        "'Starttidpunkt;Sluttidpunkt;Energiriktning;Kvalitet;Kvantitet' (E.on-export).",
    );
  }

  // Aggregera per timme. Nyckel = "YYYY-MM-DD HH" (lokal tid).
  const hourlyImport = new Map<string, number>();
  const hourlyExport = new Map<string, number>();
  let rowsParsed = 0;
  let rowsSkipped = 0;
  let estimatedCount = 0;

  for (let i = dataHeaderIdx + 1; i < lines.length; i++) {
    const line = lines[i]?.trim();
    if (!line) continue;
    const cols = line.split(";");
    if (cols.length < 5) {
      rowsSkipped++;
      continue;
    }
    const [startStr, , direction, quality, quantityStr] = cols;
    if (!startStr || !direction || !quantityStr) {
      rowsSkipped++;
      continue;
    }
    // "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DD HH"
    const hourKey = startStr.substring(0, 13); // 13 = längden på "YYYY-MM-DD HH"
    const quantity = parseFloat(quantityStr.replace(",", "."));
    if (!Number.isFinite(quantity)) {
      rowsSkipped++;
      continue;
    }
    rowsParsed++;
    if (quality === "Beräknat") estimatedCount++;

    if (direction === "Förbrukning") {
      hourlyImport.set(hourKey, (hourlyImport.get(hourKey) ?? 0) + quantity);
    } else if (direction === "Produktion") {
      hourlyExport.set(hourKey, (hourlyExport.get(hourKey) ?? 0) + quantity);
    } else {
      rowsSkipped++;
    }
  }

  if (rowsParsed === 0) {
    throw new CsvParseError(
      "Hittade inga giltiga dataposter i filen.",
    );
  }

  // Bygg HourPoint-array sorterad i tidsordning
  const allKeys = new Set([...hourlyImport.keys(), ...hourlyExport.keys()]);
  const sortedKeys = Array.from(allKeys).sort();

  const hours: HourPoint[] = sortedKeys.map((key) => {
    // Konvertera "YYYY-MM-DD HH" till ISO "YYYY-MM-DDTHH:00:00+02:00"
    // Använder Stockholm-tidszon. Vi antar standardtid (+01:00) för november-mars
    // och sommartid (+02:00) för april-oktober. Approximation över DST-övergångar
    // men nogrann nog för månadsaggregation.
    const month = parseInt(key.substring(5, 7), 10);
    const isDst = month >= 4 && month <= 10;
    const offset = isDst ? "+02:00" : "+01:00";
    const isoTimestamp = `${key.substring(0, 10)}T${key.substring(11, 13)}:00:00${offset}`;

    return {
      from: isoTimestamp,
      importKWh: Math.round((hourlyImport.get(key) ?? 0) * 1000) / 1000,
      exportKWh: Math.round((hourlyExport.get(key) ?? 0) * 1000) / 1000,
      // spotPrice fylls på efteråt från vår backend (samma som Tibber-flödet)
      spotPrice: 0,
      // Inga kvartpriser eller produktionsdata än
    };
  });

  // Räkna unika månader
  const monthKeys = new Set(sortedKeys.map((k) => k.substring(0, 7)));

  const annualImportKWh =
    Math.round(
      Array.from(hourlyImport.values()).reduce((s, v) => s + v, 0) * 10,
    ) / 10;
  const annualExportKWh =
    Math.round(
      Array.from(hourlyExport.values()).reduce((s, v) => s + v, 0) * 10,
    ) / 10;

  return {
    hours,
    rowsParsed,
    rowsSkipped,
    monthCount: monthKeys.size,
    firstTimestamp: hours[0]?.from ?? "",
    lastTimestamp: hours[hours.length - 1]?.from ?? "",
    annualImportKWh,
    annualExportKWh,
    estimatedShare: rowsParsed > 0 ? estimatedCount / rowsParsed : 0,
  };
}
